/* eslint-disable no-unused-vars */
import React, { useMemo, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { PerformerPendingBaseColumns } from "../../../utils/utils";
import UploadDocumentModal from "../../../components/UploadDocumentModal/UploadDocumentModal";
import PhysicalDocumentToggle from "../../../components/TableComponents/PhysicalDocumentToggle/PhysicalDocumentToggle";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import StatusBadge from "../../../components/TableComponents/StatusBadge/StatusBadge";
import { ROUTES } from "../../../constants";
import useTicketData  from "../../../hooks/useTicketData";
import useTicketModal from "../../../hooks/useTicketModal";
import useTicketExport from "../../../hooks/useTicketExport";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";
import { VIEW_TYPE } from "../../../utils/ticketUtils";
import GridLoadingSpinner from "../../../components/GridLoadingSpinner/GridLoadingSpinner";

function PerformerPending() {
  const history = useHistory();

  // State for ButtonGroup selection
  const [selectedView, setSelectedView] = useState(VIEW_TYPE.OPEN);
  const viewOptions = [
    { label: "Open", value: VIEW_TYPE.OPEN },
    { label: "Assigned to Me", value: VIEW_TYPE.ASSIGNED },
  ];
  const {
    searchQuery,
    setAllRows,
    allRows: filteredRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage
  } = useTicketData("RTA_APPROVED", true, selectedView);

  const {
    isModalOpen,
    selectedTicketId,
    selectedTicketNumber,
    handleOpenModal,
    handleCloseModal,
    handleOnUpload,
  } = useTicketModal();

  // Handle ButtonGroup selection
  const handleViewChange = useCallback((view) => {
    setSelectedView(view);
    setPage(0); 
  }, []);

  // Component-specific handlers
  const handlePhysicalDocumentChange = useCallback(
    (e, rowData) => {
      const newValue = e.target.checked;
      if (newValue) {
        handleOpenModal(rowData.id, rowData.ticketNumber);
      } else {
        setAllRows((prevRows) =>
          prevRows.map((row) =>
            row.ticketNumber === rowData.ticketNumber
              ? { ...row, physicalDocumentReceived: newValue }
              : row
          )
        );
      }
    },
    [handleOpenModal, setAllRows]
  );

  const handleViewPhysicalDocument = useCallback(
    (ticketNumber, ticketCode) => {
      history.push(ROUTES.GOTOQUESTIONNAIRE(ticketNumber, ticketCode));
    },
    [history]
  );

  const handleUpload = useCallback(
    (uploadData) => handleOnUpload(uploadData, setAllRows),
    [handleOnUpload, setAllRows]
  );

  // Column configuration
  const columns = useMemo(() => {
    const updatedColumns = [...PerformerPendingBaseColumns];

    updatedColumns[0] = {
      width: 280,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
          disabled={rowData.ai_agent_status === "IN_PROGRESS"}
        />
      ),
    };

    updatedColumns[3] = {
      width: 260,
      title: "Physical Document Received",
      field: "physicalDocumentReceived",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => {
        const value = rowData.physicalDocumentReceived;
        return (
          <PhysicalDocumentToggle
            checked={value}
            disabled={rowData.ai_agent_status === "IN_PROGRESS" || value}
            onChange={(e) =>
              handlePhysicalDocumentChange(e, rowData)
            }
            onViewClick={() =>
              handleViewPhysicalDocument(rowData.id, rowData.ticketNumber)
            }
          />
        );
      },
    };

    updatedColumns[4] = {
      width: 200,
      title: "AI Agent Status",
      field: "ai_agent_status",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => <StatusBadge status={rowData.ai_agent_status} />,
    };

    return updatedColumns;
  }, [handlePhysicalDocumentChange, handleViewPhysicalDocument]);

  const { handleExport, isExporting } = useTicketExport(
    "RTA_APPROVED",
    columns,
    selectedView
  );

  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}
      header={
        <Header
          searchQuery={searchQuery}
          handleSearchQuery={handleSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onExport={handleExport}
          isExporting={isExporting}
          showViewToggle
          selectedView={selectedView}
          onViewChange={handleViewChange}
          viewOptions={viewOptions}
          disableExport={filteredRows.length === 0}
        />
      }
    >
      {filteredRows.length === 0 && !(loading) ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
       )}
      <UploadDocumentModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onUpload={handleUpload}
        ticketNumber={selectedTicketNumber}
      />
    </TableLayout>
  );
}

export default PerformerPending;
